<script setup lang="ts">
import { watch, computed } from 'vue'
import { EXTERNAL_LINKS } from '@/scripts/app-configs/constants'
import useIframeCommunication from '@/composables/useIframeCommunication'
import { triggers } from '@/scripts/global-modals-commands'
import useChatIframe from '@/views/chat/composables/useChatIframe'

const props = withDefaults(defineProps<{
  modalOpen: boolean
  payload: Record<string, string>
  memberSidebarOpen: boolean
}>(), {
  modalOpen: false,
  payload: () => ({}),
  memberSidebarOpen: false,
})

const accountUuid = computed(() => props.payload.accountUuid)
const unit = computed(() => props.payload.unit)
const chatChannelUrl = computed(() => props.payload.channelId)
const isChatPopupOpened = computed(() => props.modalOpen)

const { updateIframeRoute } = useIframeCommunication()
const portalUrl = `${EXTERNAL_LINKS.uiPortal.url}/app/chat?mode=widget`

const iframeEventsHandler = ({ data: { type: eventType, payload } }: { data: { type: string; payload: { [key: string]: string } } }) => {

  // Drop event that come from different tab instances like chat in other tab
  if (eventType !== 'ready' && !isChatPopupOpened.value) return

  switch (eventType) {
    case 'open:summary-patient':
      triggers.toggleMemberSidebar({ memberUuid: payload.patientId })
      break
    case 'close:chat-popup':
      closeChatPopup()
      break
    case 'ready':
      iframeIsReady.value = true
      break
    default:
      break
  }
}

const {
  iframeRef,
  iframeIsReady,
  preferredChannelUrl,
  resetSelectedChatChannelUrl,
} = useChatIframe({ accountUuid, unit, chatChannelUrl }, iframeEventsHandler, { enableIframeUrlUpdate: isChatPopupOpened })

const closeChatPopup = () => {
  resetSelectedChatChannelUrl()
  triggers.closeChatPopup()
}

watch(preferredChannelUrl, (newUrl) => {
  const path = newUrl.replace(/^https?:\/\/[^/]+/, '')

  updateIframeRoute(iframeRef.value! as HTMLIFrameElement, path)
})
</script>

<template>
  <div id="chat-popup" :class="{ 'summary-sidebar-open': memberSidebarOpen }">
    <div :class="['chat-conversation-item', { opened: isChatPopupOpened }]">
      <div v-if="isChatPopupOpened" class="chat-conversation-item-header">
        <sword-button-wuk
          class="chat-conversation-item-header__close-button"
          theme="link"
          round
          size="small"
          icon="close"
          @click.native="closeChatPopup"
        />
      </div>
      <div v-show="isChatPopupOpened" class="chat-conversation-content">
        <iframe ref="iframeRef" :src="portalUrl" frameborder="0"></iframe>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#chat-popup {
  position: fixed;
  bottom: 1rem;
  right: 2rem;
  z-index: 50;
  display: flex;
  align-items: flex-end;
  gap: 1rem;

  &.summary-sidebar-open {
    right: 30rem;
  }
}

$chatHeight: 85vh;

.chat-conversation {
  &-item {
    box-shadow: 0px 6px 18px 0px rgba(0, 0, 0, 0.25);

    width: 265px;
    background: #fff;
    border-radius: 1rem;
    overflow: hidden;

    &.opened {
      width: 400px;
    }

    &-header {
      display: flex;
      justify-content: flex-end;
      padding: 1rem;
      border-bottom: 1px solid $context-color-7;

      &__close-button {
        position: absolute;
        top: 0;
        right: 0;

        ::v-deep {
          .sword-button-wuk.--small.round {
            margin: 0;
          }

          .sword-icon.svg-icon {
            height: 0.875rem !important;
            width: 0.875rem !important;
          }
        }
      }
    }
  }

  &-content {
    height: $chatHeight;
    display: flex;

    iframe {
      width: 100%
    }
  }
}
</style>
